import {
  AccountDescriptionSettings,
  DescriptionSettingItem,
  getDescriptionDimensionsPreview,
  resolveDescriptionSettingLines,
  SALES_DESCRIPTION_SETTINGS_ORDER,
} from './description-settings';

export type EstimateDescriptionContextOptions = {
  functionLabel?: string | null;
  isBundled?: boolean | null;
  yardsNeeded?: string | number | null;
  includeContextLine?: boolean;
};

const hasValue = (value?: string | number | null): boolean =>
  value !== undefined && value !== null && `${value}`.trim().length > 0;

const normalizeMultilineDescription = (value?: string | null): string => {
  if (typeof value !== 'string') {
    return '';
  }

  return value
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .join('\n')
    .trim();
};

const getFabricOrderOrigin = (fabric: any): number =>
  Number(
    fabric?.orderOrigin ??
      fabric?.draperyOrderFabric?.orderOrigin ??
      fabric?.draperyOrderLiningsFabric?.orderOrigin ??
      fabric?.fabric?.orderOrigin
  );

const normalizeEstimateOrderOrigin = (fabric: any): 1 | 2 => {
  return getFabricOrderOrigin(fabric) === 2 ? 2 : 1;
};

const formatYardsValue = (value?: string | number | null): string => {
  if (!hasValue(value)) {
    return '';
  }

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return `${value}`.trim();
  }

  return Number(numeric.toFixed(2)).toString();
};

const normalizeFunctionLabel = (
  fabric: any,
  explicitLabel?: string | null,
  fallbackMode: 'supplier-and-pattern' | 'pattern-only' = 'supplier-and-pattern'
): string => {
  const rawLabel =
    explicitLabel ||
    fabric?.name ||
    fabric?.draperyOrderFabric?.name ||
    fabric?.draperyOrderLiningsFabric?.name ||
    fabric?.fabric?.name ||
    '';

  const trimmed = `${rawLabel}`.trim();
  if (!trimmed) {
    return fallbackMode === 'pattern-only' && hasValue(fabric?.pattern)
      ? `${fabric.pattern}`.trim()
      : '';
  }

  return trimmed.replace(/\s+\d+$/, '').replace(/\s+/g, ' ');
};

const buildContextLine = ({
  fabric,
  contextOptions,
  fallbackMode,
}: {
  fabric: any;
  contextOptions?: EstimateDescriptionContextOptions;
  fallbackMode: 'supplier-and-pattern' | 'pattern-only';
}): string => {
  const functionLabel = normalizeFunctionLabel(fabric, contextOptions?.functionLabel, fallbackMode);
  if (!functionLabel) {
    return '';
  }

  const orderOrigin = normalizeEstimateOrderOrigin(fabric);
  const yardsNeeded =
    contextOptions?.yardsNeeded ??
    fabric?.yardsNeeded ??
    fabric?.draperyOrderFabric?.yardsNeeded ??
    fabric?.draperyOrderLiningsFabric?.yardsNeeded;
  const normalizedYards = formatYardsValue(yardsNeeded);

  if (orderOrigin === 2) {
    const dedupedFunctionLabel = functionLabel.replace(/^com(?:\s*\/|\s+|-|:)?\s*/i, '').trim();
    const normalizedFunctionLabel = dedupedFunctionLabel || functionLabel;

    if (contextOptions?.isBundled && normalizedYards) {
      return `COM ${normalizedFunctionLabel}: ${normalizedYards} yards`;
    }

    return `COM ${normalizedFunctionLabel}:`;
  }

  return `${functionLabel}:`;
};

const hasConfiguredSettings = (settings?: DescriptionSettingItem[] | null): boolean =>
  Array.isArray(settings) && settings.length > 0;

const getDescriptionDetailsFields = (item: any, fabric: any) => ({
  fabricWidthInches:
    item?.fabricWidthInches ??
    item?.fabricsOrLiningsItemDetails?.fabricWidthInches ??
    fabric?.fabricWidthInches ??
    fabric?.values?.fabricWidth ??
    fabric?.fabric?.values?.fabricWidth,
  verticalRepeatInches:
    item?.verticalRepeatInches ??
    item?.fabricsOrLiningsItemDetails?.verticalRepeatInches ??
    fabric?.verticalRepeatInches ??
    fabric?.values?.verticalRepeat ??
    fabric?.fabric?.values?.verticalRepeat,
  horizontalRepeatInches:
    item?.horizontalRepeatInches ??
    item?.fabricsOrLiningsItemDetails?.horizontalRepeatInches ??
    fabric?.horizontalRepeatInches ??
    fabric?.values?.horizontalRepeat ??
    fabric?.fabric?.values?.horizontalRepeat,
});

const getSupplierDisplayValue = (source: any): string => {
  if (!source) {
    return '';
  }

  if (typeof source?.supplierName === 'string' && source.supplierName.trim()) {
    return source.supplierName.trim();
  }

  if (typeof source?.supplier === 'string' && source.supplier.trim()) {
    return source.supplier.trim();
  }

  if (typeof source?.supplier?.displayName === 'string' && source.supplier.displayName.trim()) {
    return source.supplier.displayName.trim();
  }

  if (typeof source?.customSupplier === 'string' && source.customSupplier.trim()) {
    return source.customSupplier.trim();
  }

  return '';
};

const getPatternDisplayValue = (source: any): string => {
  if (!source) {
    return '';
  }

  const candidates = [
    source?.pattern,
    source?.draperyOrderFabric?.pattern,
    source?.draperyOrderLiningsFabric?.pattern,
    source?.fabric?.pattern,
  ];

  const resolved = candidates.find(
    (candidate) => typeof candidate === 'string' && candidate.trim().length > 0
  );

  return typeof resolved === 'string' ? resolved.trim() : '';
};

const normalizePatternKey = (value: any): string =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

const getActiveSalesDescriptionSettings = (
  descriptionSettings?: AccountDescriptionSettings | null,
  orderOrigin: 1 | 2 = 1
): DescriptionSettingItem[] => {
  if (orderOrigin === 2) {
    return hasConfiguredSettings(descriptionSettings?.salesDescriptionCom)
      ? descriptionSettings?.salesDescriptionCom || []
      : [];
  }

  return hasConfiguredSettings(descriptionSettings?.salesDescriptionWorkroom)
    ? descriptionSettings?.salesDescriptionWorkroom || []
    : [];
};

export const hasConfiguredSalesDescriptionSettings = ({
  fabric,
  descriptionSettings,
}: {
  fabric: any;
  descriptionSettings?: AccountDescriptionSettings | null;
}): boolean =>
  hasConfiguredSettings(
    getActiveSalesDescriptionSettings(descriptionSettings, normalizeEstimateOrderOrigin(fabric))
  );

// Both [] and null mean "explicitly no sales description" and should suppress legacy blocks.
export const hasExplicitSalesDescriptionSettings = ({
  fabric,
  descriptionSettings,
}: {
  fabric: any;
  descriptionSettings?: AccountDescriptionSettings | null;
}): boolean => {
  if (!descriptionSettings) return false;
  const orderOrigin = normalizeEstimateOrderOrigin(fabric);
  const settings =
    orderOrigin === 2
      ? descriptionSettings.salesDescriptionCom
      : descriptionSettings.salesDescriptionWorkroom;
  return settings !== undefined;
};

export const findPatternItemForFabric = (fabric: any, items: any[] = []) => {
  if (!fabric || !Array.isArray(items) || items.length === 0) {
    return undefined;
  }

  const targetPattern = normalizePatternKey(fabric?.pattern);
  const findByPattern = () =>
    targetPattern
      ? items.find((item) => normalizePatternKey(item?.name) === targetPattern)
      : undefined;

  const targetId = fabric?.patternId || fabric?.itemId;
  if (targetId) {
    const foundById = items.find((item) => item?.id === targetId || item?.itemId === targetId);
    if (foundById) {
      const idMatchPattern = normalizePatternKey(foundById?.name);
      if (!targetPattern || idMatchPattern === targetPattern) {
        return foundById;
      }

      return findByPattern();
    }
  }

  return findByPattern();
};

const resolveEstimateDescriptionFromSettings = ({
  fabric,
  items = [],
  descriptionSettings,
}: {
  fabric: any;
  items?: any[];
  descriptionSettings?: AccountDescriptionSettings | null;
}): { hasSettings: boolean; text: string } => {
  const item = findPatternItemForFabric(fabric, items);
  const activeSettings = getActiveSalesDescriptionSettings(
    descriptionSettings,
    normalizeEstimateOrderOrigin(fabric)
  );

  if (!hasConfiguredSettings(activeSettings)) {
    return {
      hasSettings: false,
      text: '',
    };
  }

  const valueMap = {
    supplier:
      getSupplierDisplayValue(item) ||
      getSupplierDisplayValue(fabric) ||
      getSupplierDisplayValue(fabric?.fabric) ||
      '',
    'customer-facing-name': item?.customerFacingName?.trim() || '',
    name: item?.name?.trim() || getPatternDisplayValue(fabric),
    item: item?.itemNumber || '',
    specifications: getDescriptionDimensionsPreview(getDescriptionDetailsFields(item, fabric)),
  };

  return {
    hasSettings: true,
    text: resolveDescriptionSettingLines({
      settings: activeSettings,
      valueMap,
      orderedValues: SALES_DESCRIPTION_SETTINGS_ORDER,
      fallbackSettings: activeSettings,
    }).join(' - '),
  };
};

const getFallbackSupplierLine = (fabric: any): string => {
  if (fabric?.supplierName) return fabric.supplierName;
  if (fabric?.fabric?.supplierName) return fabric.fabric.supplierName;
  if (fabric?.supplier?.displayName) return fabric.supplier.displayName;
  if (fabric?.fabric?.supplier?.displayName) return fabric.fabric.supplier.displayName;
  if (typeof fabric?.supplier === 'string' && fabric.supplier.trim()) return fabric.supplier.trim();
  if (fabric?.customSupplier) return fabric.customSupplier;
  if (fabric?.fabric?.customSupplier) return fabric.fabric.customSupplier;

  const orderOrigin = getFabricOrderOrigin(fabric);

  if (orderOrigin === 2) return 'COM';
  if (orderOrigin === 1 || orderOrigin === -1) return 'Unspecified';

  const patternPrefix =
    typeof fabric?.pattern === 'string' ? fabric.pattern.split('/')[0]?.trim() : '';
  return patternPrefix || 'Unspecified';
};

export const resolveEstimateDescriptionText = ({
  fabric,
  items = [],
}: {
  fabric: any;
  items?: any[];
}): string => {
  const item = findPatternItemForFabric(fabric, items);
  const preferredDescription =
    normalizeEstimateOrderOrigin(fabric) === 2
      ? item?.salesDescription
      : item?.salesDescriptionWorkroom;

  return normalizeMultilineDescription(preferredDescription);
};

const resolveEstimateDescriptionBodyLines = ({
  fabric,
  items = [],
  fallbackMode,
  descriptionSettings,
}: {
  fabric: any;
  items?: any[];
  fallbackMode: 'supplier-and-pattern' | 'pattern-only';
  descriptionSettings?: AccountDescriptionSettings | null;
}): string[] => {
  const item = findPatternItemForFabric(fabric, items);
  const configuredDescription = resolveEstimateDescriptionFromSettings({
    fabric,
    items,
    descriptionSettings,
  });

  if (configuredDescription.hasSettings) {
    return configuredDescription.text.split('\n').filter(Boolean);
  }

  // Explicitly empty settings ([], null) mean "show no sales description body".
  if (
    hasExplicitSalesDescriptionSettings({
      fabric,
      descriptionSettings,
    })
  ) {
    return [];
  }

  const savedDescription = resolveEstimateDescriptionText({ fabric, items });

  if (savedDescription) {
    return savedDescription.split('\n').filter(Boolean);
  }

  if (fallbackMode === 'pattern-only') {
    return [fabric?.pattern || 'Unspecified'].filter(Boolean);
  }

  const fallbackLines = [getFallbackSupplierLine(fabric), fabric?.pattern || 'Unspecified'];

  if (item?.itemNumber) {
    fallbackLines.push(item.itemNumber);
  }

  return fallbackLines.filter(Boolean);
};

export const resolveEstimateDescriptionLines = ({
  fabric,
  items = [],
  fallbackMode = 'supplier-and-pattern',
  descriptionSettings,
  contextOptions,
}: {
  fabric: any;
  items?: any[];
  fallbackMode?: 'supplier-and-pattern' | 'pattern-only';
  descriptionSettings?: AccountDescriptionSettings | null;
  contextOptions?: EstimateDescriptionContextOptions;
}): string[] => {
  const bodyLines = resolveEstimateDescriptionBodyLines({
    fabric,
    items,
    fallbackMode,
    descriptionSettings,
  });

  const shouldIncludeContextLine = contextOptions?.includeContextLine ?? fallbackMode === 'supplier-and-pattern';
  if (!shouldIncludeContextLine) {
    return bodyLines;
  }

  const contextLine = buildContextLine({
    fabric,
    contextOptions,
    fallbackMode,
  });

  if (!contextLine) {
    return bodyLines;
  }

  return [contextLine, ...bodyLines];
};

export const resolveEstimateDescriptionInline = ({
  fabric,
  items = [],
  fallbackMode = 'pattern-only',
  descriptionSettings,
  contextOptions,
}: {
  fabric: any;
  items?: any[];
  fallbackMode?: 'supplier-and-pattern' | 'pattern-only';
  descriptionSettings?: AccountDescriptionSettings | null;
  contextOptions?: EstimateDescriptionContextOptions;
}): string => {
  return resolveEstimateDescriptionLines({
    fabric,
    items,
    fallbackMode,
    descriptionSettings,
    contextOptions,
  }).join(' / ');
};
